import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import H from '@here/maps-api-for-javascript';
import getCountryISO2 from 'country-iso-3-to-2';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';
import { fitBoundsFromBbox } from 'components/Map/NewJobMarkers/utils/markerHelpers';
import withTranslation from '../../hoc/withTranslation';
import { TIMEOUT_VALUES, setTerritoryParameter } from '../../actions';
import '@here/maps-api-for-javascript/bin/mapsjs-ui.css';
import {
  createBaseLayers,
  createMap,
  createSettingsControl,
  createUI,
  setUnitSystem,
} from '../../utils/map/HereMapUtils';
import { getBounds } from '../../utils/map/MapCalculations';
import { getLastValidIteration, getToursDataFromIteration } from '../../utils/SolutionHelpers';
import { getAreaPolygonFormat } from '../../utils/territories/TerritoryAreaConverter';
import { getTerritoriesGeoJSON } from '../../utils/territories/TerritoriesHelpers';
import {
  generateAllTourConcaveHull,
  generateAllTourConvexHull,
} from '../../utils/territories/TerritoriesFromJson';
import { APP_MODES, getAppMode } from '../../utils/urlHelpers';
import NewMapMarker from './MapMarkers/NewMapMarker';
import HereLocationSelector from './HereLocationSelector';
import NewMapTourLines from './NewTourLines';
import NewMapAreas from './NewMapAreas';

const StyledMap = styled.div({
  width: '100%',
  overflow: 'hidden',
  position: 'fixed',
  height: 'calc(100vh - 3rem)',
});

const engineType = H.Map.EngineType.HARP;
const appMode = getAppMode();
const terraFF = appMode.includes(APP_MODES.TERRA);

const NewMap = ({ user, oAuth, display, mapData }) => {
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const mapUIRef = useRef(null);
  const settingsControlRef = useRef(null);
  const map = useRef(null);
  const boundsTimerRef = useRef(null);
  const prevBoundsRef = useRef(null);
  const currentStep = useSelector((state) => state.usageContext.currentStep);
  const solution = useSelector(({ solution: s }) => s);
  const { territories, groupAreas, avoidAreas, excludeAreas } = useSelector(
    (state) => state.areas[solution.show],
  );

  const filtered = useMemo(
    () => getLastValidIteration(solution.requests[solution.show]),
    [solution],
  );
  const tourData = useMemo(() => getToursDataFromIteration(filtered), [filtered]);

  const { showTourConvexHull, showTourConcaveHull } = user.mapSettings;
  const validStep = [3, 4].includes(currentStep);

  const [tourHulls, setTourHulls] = useState([]);
  const [areaPolygon, setAreaPolygon] = useState(null);

  const ISO2Code = getCountryISO2(user.politicalView);
  const mapLayers = useMemo(
    () => createBaseLayers(engineType, user.language, ISO2Code),
    [user.language, ISO2Code],
  );

  useEffect(() => {
    if (!map.current) {
      map.current = createMap(mapLayers, mapRef, engineType);
      const settingsControl = createSettingsControl(mapLayers);
      mapUIRef.current = createUI(mapLayers, map.current, settingsControl);
      settingsControlRef.current = settingsControl;
      setUnitSystem(mapUIRef.current, user.distance);
    }
  }, [mapLayers, user.distance]);

  useEffect(() => {
    return () => {
      if (map.current?._resizeHandler) {
        window.removeEventListener('resize', map.current._resizeHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (mapUIRef.current) setUnitSystem(mapUIRef.current, user.distance);
  }, [user.distance]);

  useEffect(() => {
    if (map.current && !display.noMapMovement) {
      const newBounds = getBounds(mapData, user, display, null);

      if (!isEqual(newBounds, prevBoundsRef.current)) {
        prevBoundsRef.current = newBounds;

        if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);

        boundsTimerRef.current = setTimeout(() => {
          fitBoundsFromBbox(map.current, newBounds);
          boundsTimerRef.current = null;
        }, TIMEOUT_VALUES.HIGH);
      }
    }

    return () => {
      if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
    };
  }, [
    mapData,
    display.showProblem,
    display.showUnassigned,
    display.selectedTour,
    display.noMapMovement,
    display.routeIds,
  ]);

  useEffect(() => {
    if (showTourConvexHull && validStep) {
      setTourHulls(generateAllTourConvexHull(tourData));
    } else if (showTourConcaveHull?.enabled && validStep) {
      setTourHulls(generateAllTourConcaveHull(tourData, showTourConcaveHull.value));
    } else {
      setTourHulls([]);
    }
  }, [tourData, showTourConvexHull, showTourConcaveHull, validStep]);

  const showTourHulls = tourHulls.length > 0 && !display.showUnassigned;

  useEffect(() => {
    if (terraFF) {
      setAreaPolygon(getAreaPolygonFormat(territories.areaDetails));
    } else {
      setAreaPolygon(
        getTerritoriesGeoJSON(
          territories.areaDetails,
          avoidAreas.areaDetails,
          groupAreas.areaDetails,
          excludeAreas?.areaDetails,
          showTourHulls ? tourHulls : [],
          { ...tourData, tourId: display.selectedTour },
        ),
      );
    }
  }, [
    territories,
    groupAreas,
    avoidAreas,
    excludeAreas,
    tourHulls,
    showTourHulls,
    tourData,
    display.selectedTour,
  ]);

  const showMapAreas = areaPolygon != null && (territories.isEnabled || showTourHulls);

  const handleAreaClick = useCallback(
    (properties) => {
      const selectedAreaValue = properties.postal_code;
      if (!selectedAreaValue) return;
      dispatch(setTerritoryParameter({ selectedAreas: { value: selectedAreaValue } }));
    },
    [dispatch],
  );

  return (
    <StyledMap ref={mapRef}>
      {map.current && (
        <>
          <NewMapMarker
            display={display}
            map={map.current}
            mapData={mapData}
            ui={mapUIRef}
            user={user}
            oAuth={oAuth}
          />
          <HereLocationSelector
            map={map.current}
            oAuth={oAuth}
            display={display}
            allowRightClick={currentStep === 2}
          />
          <NewMapTourLines map={map.current} user={user} oAuth={oAuth} mapData={mapData} />
          {showMapAreas && (
            <NewMapAreas
              map={map.current}
              areaPolygon={areaPolygon}
              onClick={handleAreaClick}
              tourData={tourData}
            />
          )}
        </>
      )}
    </StyledMap>
  );
};

export default withTranslation(NewMap);
